import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { motion, AnimatePresence } from "framer-motion";
import {
  Globe2,
  Users,
  Shield,
  Zap,
  BarChart3,
  Sword,
  BrainCircuit,
  Play,
  CheckCircle2,
  ChevronDown,
  Mail,
  Sparkles,
  ExternalLink,
  Menu,
  X,
  Quote,
  Terminal,
  AlertTriangle,
  Fingerprint
} from "lucide-react";
import game from "./assets/landing/view-game.png";
import stat from "./assets/landing/view-stat.png";
import decret from "./assets/landing/view-decret.png";

// --- Mini-Jeu Narratif Component ---
const InteractiveTeaser = () => {
  const [step, setStep] = useState("intro"); // intro, action, result
  const [result, setResult] = useState(null);
  const [scenarioIndex, setScenarioIndex] = useState(0);

  const scenarios = useMemo(() => [
    {
      id: "nuclear_ambition",
      title: "Dilemme Nucléaire",
      desc: "Ah, le mantra du stratège moderne : « nucléaire, nucléaire, nucléaire ! » J’espère que tu as prévu un bunker Instagrammable, parce que vu tes moyens, tu vas plus faire clignoter la lumière que déclencher la fin du monde.",
      choices: [
        {
          id: "nuke",
          label: "Investir Nucléaire",
          icon: <Zap className="w-4 h-4" />,
          color: "from-yellow-500 to-orange-500",
          desc: "Décret exécutif n°9982/BrilleDansLeNoir : Lancement d’un gigantesque plan d’investissement. Objectif : dépasser le stade de la lampe torche.",
          feedback: "Choix lumineux ! Vos voisins achètent des pastilles d'iode. Votre cote de popularité brille... par son absence.",
          stat: "Menace +25, Budget -10Md"
        },
        {
          id: "tech",
          label: "Investir Technologie",
          icon: <BrainCircuit className="w-4 h-4" />,
          color: "from-blue-500 to-cyan-500",
          desc: "Décret exécutif n°4115/ÉtronFuturiste : Claquer quelques brouzoufs dans la technologie. La prochaine génération de boutons brillera en Bluetooth.",
          feedback: "Sage décision. On ne domine pas le monde avec des lance-pierres, même radioactifs. Vos ingénieurs sourient enfin.",
          stat: "Techno +15, Électricité -5%"
        }
      ]
    },
    {
      id: "greedy_investor",
      title: "Le Panier Percé",
      desc: "Investir partout comme si tu avais la carte bancaire illimitée de ta mamie ? Pourquoi pas ! Tu veux muscler l'économie, agiter des tubes à essai radioactifs et faire défiler l'armée... La vie, c'est pas un buffet à volonté.",
      choices: [
        {
          id: "eco",
          label: "Relance Économique",
          icon: <BarChart3 className="w-4 h-4" />,
          color: "from-green-500 to-emerald-500",
          desc: "Décret n°8943/CrashBoursier : Injecter des fonds dans l’économie nationale plutôt que dans les yachts ministériels.",
          feedback: "L'économie frémit. C'est peut-être un soubresaut avant l'arrêt cardiaque, mais c'est positif !",
          stat: "PIB +2%, Inflation +4%"
        },
        {
          id: "military",
          label: "Parade Militaire",
          icon: <Sword className="w-4 h-4" />,
          color: "from-red-500 to-rose-500",
          desc: "Décret n°2743/PompesEtCirculez : Subventionner l’armée, histoire que tes soldats aient autre chose qu’un lance-pierre pour la parade.",
          feedback: "Vos soldats ont de nouvelles bottes. Ils marchent toujours n'importe comment, mais avec style.",
          stat: "Force +10, Stabilité -2"
        },
        {
          id: "nuke",
          label: "Programme Atome",
          icon: <Zap className="w-4 h-4" />,
          color: "from-purple-500 to-violet-500",
          desc: "Décret n°6677/AtomesCrochus : Remplir la tirelire du nucléaire afin que tes voisins aient toujours une excuse pour mal dormir.",
          feedback: "Message reçu 5 sur 5 par le monde entier. Surtout le message 'Je suis instable et dangereux'.",
          stat: "Tension Mondiale +20"
        }
      ]
    },
    {
      id: "suicide_mission",
      title: "David contre Goliath",
      desc: "Ah, attaquer Zhongo, tu as le goût du risque... ou une envie soudaine de goûter à la vraie définition du mot 'problème'. Entre leur technologie supérieure et une stabilité à faire pâlir ton banquier, c'est comme tenter d'arracher un steak à un tigre.",
      choices: [
        {
          id: "attack",
          label: "Attaquer Zhongo",
          icon: <Sword className="w-4 h-4" />,
          color: "from-red-600 to-orange-700",
          desc: "Décret n°7723/Courage-Fuyons : Lancer bravement les hostilités, en espérant qu'ils soient trop occupés à compter leurs robots.",
          feedback: "Aïe. Leurs drones ont intercepté vos pigeons voyageurs avant même le début de l'attaque. Retraite !",
          stat: "Armée -50%, Dignité -100%"
        },
        {
          id: "tech",
          label: "Investir Tech",
          icon: <Shield className="w-4 h-4" />,
          color: "from-indigo-500 to-blue-500",
          desc: "Décret n°9912/RetardTech : Injecter un peu de fonds dans la technologie, histoire de ne pas envoyer nos soldats au front avec des lance-pierres.",
          feedback: "Vous évitez le massacre. Pour l'instant. Vos chercheurs ont découvert le 'Feu' (version 2.0).",
          stat: "Techno +5, Survie +100%"
        }
      ]
    }
  ], []);

  // Select random scenario on mount
  useEffect(() => {
    setScenarioIndex(Math.floor(Math.random() * scenarios.length));
  }, [scenarios.length]);

  const currentScenario = scenarios[scenarioIndex];

  const handleChoice = (choice) => {
    setResult(choice);
    setStep("result");
  };

  const nextScenario = () => {
    setStep("intro");
    setResult(null);
    setScenarioIndex((prev) => (prev + 1) % scenarios.length);
  };

  return (
    <div className="mt-8 max-w-md w-full mx-auto font-sans z-20 relative">
      <div className="relative overflow-hidden rounded-2xl border border-white/20 bg-black/60 backdrop-blur-xl shadow-2xl ring-1 ring-white/10">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-violet-500 to-transparent opacity-50" />

        <div className="p-6">
          <AnimatePresence mode="wait">
            {step === "intro" && (
              <motion.div
                key={`intro-${currentScenario.id}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <div className="flex items-center gap-2 mb-3 text-amber-400 text-xs font-bold uppercase tracking-widest">
                  <AlertTriangle className="w-4 h-4" /> Situation Critique
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{currentScenario.title}</h3>
                <p className="text-white/70 text-sm mb-6 leading-relaxed border-l-2 border-white/10 pl-3 italic">
                  "{currentScenario.desc}"
                </p>

                <div className="grid gap-3">
                  {currentScenario.choices.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => handleChoice(c)}
                      className="group relative block w-full text-left p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/20 transition-all"
                    >
                      <div className="flex items-center gap-3 mb-1">
                        <div className={`p-1.5 rounded-lg bg-gradient-to-br ${c.color} opacity-80 group-hover:opacity-100 shadow-lg`}>
                          {c.icon}
                        </div>
                        <span className="text-white/90 font-bold text-sm group-hover:text-white">{c.label}</span>
                      </div>
                      <div className="text-[10px] text-white/40 pl-[38px] leading-tight line-clamp-2">
                        {c.desc}
                      </div>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {step === "result" && result && (
              <motion.div
                key="result"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center border border-white/20 shadow-inner">
                    <BrainCircuit className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-xs text-indigo-300 font-semibold uppercase">Analyse de l'IA</div>
                    <div className="text-white text-sm font-bold">Conséquence Immédiate</div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-4 border border-white/10 mb-4 relative overflow-hidden">
                  <div className="absolute -right-4 -top-4 w-16 h-16 bg-white/5 rounded-full blur-2xl" />
                  <p className="text-white/80 italic text-sm leading-relaxed relative z-10">
                    "{result.feedback}"
                  </p>
                </div>

                <div className="flex justify-between items-center text-xs font-mono text-white/50 mb-6 bg-black/20 p-2 rounded-lg border border-white/5">
                  <span className="uppercase tracking-wider">Impact:</span>
                  <span className={`${result.id.includes('nuke') || result.id === 'attack' ? 'text-red-400' : 'text-green-400'} font-bold`}>
                    {result.stat}
                  </span>
                </div>

                <button
                  onClick={nextScenario}
                  className="w-full py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors flex items-center justify-center gap-2 group border border-white/5"
                >
                  <Fingerprint className="w-4 h-4 opacity-50 group-hover:opacity-100" /> Scénario Suivant
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};


export default function LandingGeopolitique() {
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [faqOpen, setFaqOpen] = useState(null);
  const [currentShot, setCurrentShot] = useState(0);
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);

  // Animations variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  const staggerContainer = {
    visible: { transition: { staggerChildren: 0.1 } }
  };

  const cardsFloat = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: "easeOut"
      }
    })
  };

  const aiQuotes = [
    {
      category: "La diplomatie du \"Bricolage\"",
      text: "\"Bricologia tente l’effet spectaculaire (ou pathétique ?) en attaquant Zhongo… Avec des flyers et trois tractopelles !\""
    },
    {
      category: "La diplomatie du \"Bricolage\"",
      text: "\"Bricologia s’essaie au nucléaire façon ‘Atome pour les Nuls’... la principale avancée reste le stockage du plutonium dans des bocaux à confiture.\""
    },
    {
      category: "La satire technologique",
      text: "\"Zhongo investit dans la technologie pour s'assurer que personne ne gagne à Candy Crush sans autorisation impériale.\""
    },
    {
      category: "Le désespoir du Pétrolistan",
      text: "\"Petrolistan découvre que la technologie n'est pas que des lampes à huile... et tente de brancher son gouverneur sur secteur.\""
    },
    {
      category: "La paranoïa d'Eurocorp",
      text: "\"Eurocorp se prend pour Elon Musk... tout en se donnant des airs de Robocop ; personne n’est impressionné, à part leurs actionnaires dopés au café.\""
    }
  ];

  const screenshots = useMemo(
    () => [
      { src: game, alt: "Carte géopolitique – vue monde" },
      { src: stat, alt: "Statistiques et rapports" },
      { src: decret, alt: "Decrets et décisions politiques" },
    ],
    []
  );

  const features = [
    {
      icon: <BrainCircuit className="w-6 h-6" />,
      title: "IA réactive",
      desc: "Des adversaires qui s’adaptent à vos choix et une simulation crédible du monde.",
    },
    {
      icon: <Globe2 className="w-6 h-6" />,
      title: "Carte vivante",
      desc: "Territoires, ressources, climat et population influencent chaque décision.",
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Diplomatie avancée",
      desc: "Alliances, trahisons, traités de paix, sanctions… écrivez votre doctrine.",
    },
    {
      icon: <Sword className="w-6 h-6" />,
      title: "Conflits & espionnage",
      desc: "Opérations secrètes, guerres éclair, défense anti‑missile, cyber actions.",
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Économie dynamique",
      desc: "Budget, commerce, techs, volatilité des marchés et chocs globaux.",
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Stabilité intérieure",
      desc: "Opinion publique, risques, catastrophes naturelles et résilience.",
    },
  ];

  const faqs = [
    {
      q: "Le jeu est-il jouable sur mobile ?",
      a: "Oui, l’interface est responsive. La démo web est prévue pour desktop et mobile modernes.",
    },
    {
      q: "Y a‑t‑il un mode multijoueur ?",
      a: "La feuille de route inclut un mode tour par tour asynchrone et des parties privées entre amis.",
    },
    {
      q: "Combien de temps dure une partie ?",
      a: "De 15 à 60 minutes pour la démo, selon la taille de la carte et le nombre d’événements.",
    },
    {
      q: "Le contenu est‑il mis à jour ?",
      a: "Oui : nouveaux événements, équilibrages, arbres techno et scénarios.",
    },
  ];

  const handleNewsletter = async (e) => {
    e.preventDefault();
    try {
      const token = await executeRecaptcha("newsletter");
      if (!token) {
        alert("Erreur de captcha");
        return;
      }
      // Implementation fictive pour la démo
      alert("Merci ! Tu es inscrit (Simulation).");
    } catch (error) {
      console.error("Erreur newsletter:", error);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#0b1020] text-white overflow-x-hidden">

      {/* Background Effects */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-indigo-900/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-violet-900/20 rounded-full blur-[120px]" />
        <div className="absolute top-[40%] left-[50%] -translate-x-1/2 w-[60%] h-[60%] bg-blue-900/10 rounded-full blur-[100px]" />
      </div>

      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-black/40 border-b border-white/5 supports-[backdrop-filter]:bg-black/20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button onClick={() => navigate("/")} className="relative z-10 transition-transform hover:scale-105">
              <img src="/logoGeo.svg" alt="GeoPolitikIA logo" width={300} height={64} className="h-8 w-auto md:h-20" />
            </button>
          </div>
          <div className="flex items-center gap-3">
            <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-white/70">
              {['Fonctionnalités', 'Images', 'Tarifs', 'FAQ'].map((item) => (
                <a key={item} href={`#${item.toLowerCase().replace('é', 'e')}`} className="hover:text-white hover:drop-shadow-[0_0_8px_rgba(255,255,255,0.5)] transition-all">
                  {item}
                </a>
              ))}
            </nav>
            <div className="hidden md:flex items-center gap-3">
              <a
                href="#pricing"
                className="group relative inline-flex items-center gap-2 rounded-xl bg-white/5 overflow-hidden px-4 py-2 text-sm font-semibold border border-white/10 hover:border-violet-400/50 transition-all shadow-[0_0_20px_-10px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_-5px_rgba(139,92,246,0.5)]"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/20 via-violet-500/20 to-blue-500/20 opacity-0 group-hover:opacity-100 transition-opacity" />
                <Sparkles className="w-4 h-4 text-violet-300" /> <span className="relative">Acheter</span>
              </a>
              <a
                href="/login"
                className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold shadow-lg shadow-violet-500/20 hover:bg-violet-500 hover:shadow-violet-500/40 transition-all active:scale-95"
              >
                <Users className="w-4 h-4" /> Connexion
              </a>
            </div>
            <button
              className="md:hidden p-2 text-white/80"
              onClick={() => setMobileMenuOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            className="md:hidden fixed inset-0 z-50 bg-[#0b1020]/95 backdrop-blur-xl flex flex-col items-center justify-center gap-8 text-xl"
          >
            <button className="absolute top-4 right-4 p-4 text-white/50 hover:text-white" onClick={() => setMobileMenuOpen(false)}>
              <X className="w-8 h-8" />
            </button>
            {['Fonctionnalités', 'Images', 'Tarifs', 'FAQ'].map((item) => (
              <a key={item} href={`#${item.toLowerCase().replace('é', 'e')}`} onClick={() => setMobileMenuOpen(false)} className="hover:text-violet-400 font-semibold tracking-wide">
                {item}
              </a>
            ))}
            <div className="flex flex-col gap-4 mt-8 w-64">
              <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="bg-white/10 border border-white/10 py-3 rounded-xl text-center font-bold">Acheter maintenant</a>
              <a href="/login" onClick={() => setMobileMenuOpen(false)} className="bg-violet-600 py-3 rounded-xl text-center font-bold shadow-lg shadow-violet-900/50">Connexion</a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-16 pb-20 md:pt-32 md:pb-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">

            {/* Left Column: Text & CTA */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >

              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.1] tracking-tight">
                Dominez <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-violet-400 to-cyan-400 animate-gradient-x">
                  le Monde
                </span>
              </h1>

              <p className="mt-6 text-lg sm:text-xl text-white/60 max-w-xl leading-relaxed">
                Diplomatie, espionnage, guerre nucléaire. <br />
                Une IA réactive commente chacun de vos échecs.
              </p>

              <div className="mt-8 flex flex-wrap gap-4">
                <a href="/login" className="relative group inline-flex items-center justify-center gap-3 rounded-xl bg-white text-black px-8 py-4 text-base font-bold shadow-xl hover:bg-gray-100 transition-all hover:scale-[1.02] active:scale-[0.98]">
                  <Play className="w-5 h-5 fill-current" />
                  Jouer la démo
                  <div className="absolute inset-0 rounded-xl ring-2 ring-white/50 opacity-0 group-hover:opacity-100 transition-opacity animate-pulse" />
                </a>
                <a href="#trailer" className="inline-flex items-center justify-center gap-3 rounded-xl border border-white/20 bg-white/5 backdrop-blur-sm px-8 py-4 text-base font-bold hover:bg-white/10 transition-all">
                  <ExternalLink className="w-5 h-5" /> Trailer
                </a>
              </div>

              {/* Idea 4: Interactive Teaser inside Hero */}
              <InteractiveTeaser />

            </motion.div>

            {/* Right Column: Visuals */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8, rotate: 5 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 1, delay: 0.2, type: "spring" }}
              className="relative lg:h-[600px] flex items-center justify-center perspective-1000"
            >
              {/* Decorative glows */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-tr from-indigo-500/20 to-purple-500/20 rounded-full blur-[80px] animate-pulse-slow" />

              {/* Main image container with glass effect */}
              <div className="relative w-full max-w-lg aspect-[4/3] rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm shadow-2xl overflow-hidden group">
                <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-transparent to-transparent z-10" />
                <img
                  src={screenshots[0].src}
                  alt="Map"
                  className="w-full h-full object-cover transition-transform duration-[10s] ease-linear group-hover:scale-110"
                />

                {/* Floating UI Elements (Parallax feel) */}
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
                  className="absolute bottom-6 left-6 z-20 bg-black/60 backdrop-blur-xl border border-white/10 p-3 rounded-xl flex items-center gap-3 shadow-xl"
                >
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <div>
                    <div className="text-[10px] text-white/50 uppercase font-bold">Confiance Marchés</div>
                    <div className="text-sm font-bold text-green-400">+12.4%</div>
                  </div>
                </motion.div>

                <motion.div
                  animate={{ y: [0, 15, 0] }}
                  transition={{ repeat: Infinity, duration: 5, ease: "easeInOut", delay: 1 }}
                  className="absolute top-6 right-6 z-20 bg-black/60 backdrop-blur-xl border border-white/10 p-3 rounded-xl flex flex-col shadow-xl max-w-[150px]"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Terminal className="w-3 h-3 text-violet-400" />
                    <div className="text-[10px] text-white/50 uppercase font-bold">IA Log</div>
                  </div>
                  <div className="text-[10px] text-white/80 leading-tight">
                    "Zhongo prépare une cyber-attaque..."
                  </div>
                </motion.div>
              </div>

              {/* Secondary images floating */}
              <motion.img
                animate={{ y: [0, -20, 0], rotate: [-6, -4, -6] }}
                transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
                src={screenshots[1].src}
                className="absolute -bottom-10 -left-10 w-48 rounded-lg border border-white/20 shadow-2xl hidden md:block"
              />
              <motion.img
                animate={{ y: [0, 20, 0], rotate: [6, 8, 6] }}
                transition={{ repeat: Infinity, duration: 7, ease: "easeInOut", delay: 0.5 }}
                src={screenshots[2].src}
                className="absolute -top-10 -right-10 w-48 rounded-lg border border-white/20 shadow-2xl hidden md:block"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trailer (placeholder) */}
      <section id="trailer" className="py-16 sm:py-24 bg-[#0b1020]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          {/* Bordure extérieure décorative */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4 shadow-2xl">

            {/* Conteneur Aspect Ratio - C'est ici que la magie opère */}
            <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-black shadow-inner">

              {/* Iframe en position absolue pour remplir tout le conteneur */}
              <iframe
                className="absolute inset-0 h-full w-full"
                src="https://www.youtube.com/embed/3b4OyOwi6pA?si=ewNucOzkaP8Ww4R4"
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              ></iframe>

            </div>
          </div>
        </div>
      </section>

      {/* Features - Scroll Animation */}
      <section id="features" className="py-24 relative">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="mb-16 max-w-2xl"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-white/80">
              L'Art de la Guerre <span className="text-violet-400">Moderne</span>
            </h2>
            <p className="text-white/60 text-lg">
              Une profondeur stratégique inspirée des wargames classiques, modernisée par une interface fluide et une IA imprévisible.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                custom={i}
                variants={cardsFloat}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                className="group relative p-8 rounded-3xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition-colors overflow-hidden"
              >
                {/* Glow effect on hover */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-br from-violet-500/10 via-transparent to-transparent pointer-events-none" />

                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <div className="text-indigo-400 group-hover:text-indigo-300 transition-colors">
                    {f.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{f.title}</h3>
                <p className="text-white/60 leading-relaxed text-sm">{f.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Gallery */}
      <section id="gallery" className="py-24 bg-gradient-to-b from-black/0 to-black/30">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row justify-between items-end gap-6 mb-10"
          >
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-2 text-white/80">Immersion Totale</h2>
              <p className="text-white/60">Explorez une interface conçue comme un véritable OS.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setCurrentShot(s => (s - 1 + screenshots.length) % screenshots.length)} className="p-3 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition">←</button>
              <button onClick={() => setCurrentShot(s => (s + 1) % screenshots.length)} className="p-3 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition">→</button>
            </div>
          </motion.div>

          <div className="relative aspect-video rounded-3xl overflow-hidden border border-white/10 bg-black/50 shadow-2xl">
            <AnimatePresence mode="wait">
              <motion.img
                key={currentShot}
                initial={{ opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                src={screenshots[currentShot].src}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </AnimatePresence>

            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
          </div>

          <div className="flex justify-center gap-3 mt-6">
            {screenshots.map((s, i) => (
              <button
                key={i}
                onClick={() => setCurrentShot(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === currentShot ? 'w-8 bg-violet-500' : 'w-2 bg-white/20 hover:bg-white/40'}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Quotes - AI Humor */}
      <section className="py-24">
        <div className="mx-auto max-w-5xl px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative rounded-[2rem] border border-white/10 bg-white/[0.02] p-8 md:p-16 text-center overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-violet-500/50 to-transparent" />
            <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] bg-violet-600/10 blur-[80px]" />

            <Quote className="w-12 h-12 text-violet-500/30 mx-auto mb-8" />

            <AnimatePresence mode="wait">
              <motion.div
                key={currentQuoteIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <p className="text-2xl md:text-4xl font-serif italic text-white/90 leading-tight mb-6">
                  {aiQuotes[currentQuoteIndex].text}
                </p>
                <div className="inline-block px-4 py-1 rounded-full border border-white/10 bg-white/5 text-sm text-violet-300">
                  IA - {aiQuotes[currentQuoteIndex].category}
                </div>
              </motion.div>
            </AnimatePresence>

            <button
              onClick={() => setCurrentQuoteIndex(i => (i + 1) % aiQuotes.length)}
              className="mt-10 text-white/40 hover:text-white text-sm uppercase tracking-widest font-bold transition-colors"
            >
              Citation Suivante →
            </button>
          </motion.div>
        </div>
      </section>

      {/* Pricing - Glassmorphism Cards */}
      <section id="pricing" className="py-24 relative">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 px-4">
            <h2 className="text-3xl sm:text-5xl font-bold mb-4 text-white/80">Accès Immédiat</h2>
            <p className="text-white/60">Rejoignez l'accès anticipé et influencez le développement.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Free Tier */}
            <motion.div
              whileHover={{ y: -5 }}
              className="p-8 rounded-3xl border border-white/10 bg-white/[0.02] flex flex-col"
            >
              <h3 className="text-lg font-bold text-white/80">Découverte</h3>
              <div className="mt-4 mb-2">
                <span className="text-4xl font-bold text-white">Gratuit</span>
              </div>
              <p className="text-sm text-white/50 mb-8">Pour tester la mécanique.</p>
              <div className="space-y-4 mb-8 flex-1">
                {['1 Scénario', 'Parties Locales', 'Pub avec modération'].map(item => (
                  <div key={item} className="flex gap-3 text-sm text-white/70">
                    <CheckCircle2 className="w-5 h-5 text-white/20" /> {item}
                  </div>
                ))}
              </div>
              <button className="w-full py-3 rounded-xl border border-white/20 bg-white/5 hover:bg-white/10 font-bold transition-colors">Essayer</button>
            </motion.div>

            {/* Pro Tier (Highlighted) */}
            <motion.div
              whileHover={{ y: -10 }}
              className="relative p-8 rounded-3xl border border-violet-500/50 bg-violet-500/10 flex flex-col shadow-2xl shadow-violet-900/20"
            >
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-violet-500 text-white text-xs font-bold uppercase tracking-wider rounded-full shadow-lg">
                Populaire
              </div>
              <h3 className="text-lg font-bold text-violet-300">Stratège</h3>
              <div className="mt-4 mb-2">
                <span className="text-4xl font-bold text-white">X €</span><span className="text-white/50">/mois</span>
              </div>
              <p className="text-sm text-white/50 mb-8">L'expérience complète.</p>
              <div className="space-y-4 mb-8 flex-1">
                {['Tout illimité', 'Mode Multijoueur', 'Éditeur de scénarios', 'Badge Fondateur'].map(item => (
                  <div key={item} className="flex gap-3 text-sm text-white">
                    <CheckCircle2 className="w-5 h-5 text-violet-400" /> {item}
                  </div>
                ))}
              </div>
              <button className="w-full py-3 rounded-xl bg-violet-600 hover:bg-violet-500 font-bold transition-colors shadow-lg shadow-violet-500/25">Choisir</button>
            </motion.div>

            {/* Team Tier */}
            <motion.div
              whileHover={{ y: -5 }}
              className="p-8 rounded-3xl border border-white/10 bg-white/[0.02] flex flex-col"
            >
              <h3 className="text-lg font-bold text-white/80">Supporter</h3>
              <div className="mt-4 mb-2">
                <span className="text-4xl font-bold text-white">XX €</span> <span className="text-xs text-white/40 uppercase bg-white/10 px-2 py-1 rounded">One shot</span>
              </div>
              <p className="text-sm text-white/50 mb-8">Soutenez le créateur.</p>
              <div className="space-y-4 mb-8 flex-1">
                {['Accès Stratège à vie', 'Nom dans les crédits', 'Accès Alpha privée', 'Discord VIP'].map(item => (
                  <div key={item} className="flex gap-3 text-sm text-white/70">
                    <CheckCircle2 className="w-5 h-5 text-white/20" /> {item}
                  </div>
                ))}
              </div>
              <button className="w-full py-3 rounded-xl border border-white/20 bg-white/5 hover:bg-white/10 font-bold transition-colors">Soutenir</button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-violet-900/10"></div>
        <div className="mx-auto max-w-3xl px-4 relative z-10 text-center">
          <Mail className="w-12 h-12 text-white/20 mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-4">Ne ratez pas le lancement</h2>
          <p className="text-white/60 mb-8">Inscrivez-vous pour recevoir une notification lors de la sortie de la beta publique.</p>

          <form onSubmit={handleNewsletter} className="max-w-md mx-auto flex flex-col gap-4">
            <div className="flex gap-2">
              <input
                type="email"
                required
                placeholder="votre@email.com"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all"
              />
              <button
                className="bg-white text-black font-bold px-6 py-3 rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Go
              </button>
              <div className="flex justify-center">
                {/* reCAPTCHA v3 invisible */}
              </div>
              <p className="text-[10px] text-white/30 mt-2 text-center w-full">
                Protégé par reCAPTCHA : <a href="https://policies.google.com/privacy" className="hover:text-white">Confidentialité</a> & <a href="https://policies.google.com/terms" className="hover:text-white">Conditions</a>
              </p>
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12 bg-black/40 text-sm">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="text-white/40">
            © {new Date().getFullYear()} GeoPolitikIA. Tous droits réservés.
          </div>
          <div className="flex gap-6 text-white/60">
            <button onClick={() => setLegalOpen(true)} className="hover:text-white transition">Mentions Légales</button>
          </div>
        </div>
      </footer>

      {/* Legal Modal */}
      <AnimatePresence>
        {legalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={() => setLegalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#0b1020] border border-white/10 rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6 shadow-2xl relative"
            >
              <button
                onClick={() => setLegalOpen(false)}
                className="absolute top-4 right-4 text-white/50 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>

              <h2 className="text-2xl font-bold mb-6 text-white">Mentions Légales</h2>

              <div className="space-y-4 text-gray-300 text-sm">
                <section>
                  <h3 className="text-white font-semibold mb-1">1. Éditeur</h3>
                  <p className="text-white/60">Le site GeoPolitikIA est édité à titre personnel.</p>
                  <p className="text-white/60">Contact : adriengiguelay@gmail.com</p>
                </section>

                <section>
                  <h3 className="text-white font-semibold mb-1">2. Hébergement</h3>
                  <p className="text-white/60">Ce site est hébergé par Vercel / Render et BDD Neon</p>
                </section>

                <section>
                  <h3 className="text-white font-semibold mb-1">3. Propriété Intellectuelle</h3>
                  <p className="text-white/60">L'ensemble du contenu (textes, images, code) est la propriété exclusive de GeoPolitikIA, sauf mention contraire.</p>
                </section>

                <section>
                  <h3 className="text-white font-semibold mb-1">4. Données Personnelles</h3>
                  <p className="text-white/60">Aucune donnée personnelle n'est collectée à des fins commerciales. L'email collecté pour la newsletter sert uniquement à l'envoi d'informations sur le jeu.</p>
                </section>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
